import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog'
import type { PendingPosSale } from './offline/types'
import { plainLanguageForPosError } from './offline/errorMap'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  items: PendingPosSale[]
  isOnline: boolean
  syncInProgress: boolean
  lastSyncAttemptIso: string | null
  onRetry: (localId: string) => void
  onRetryAllFailed: () => void
  onRemove: (localId: string) => void
  onApplyStockFix: (localId: string) => void
}

function shortRef(idempotencyKey: string): string {
  return idempotencyKey.replace(/-/g, '').slice(-6).toUpperCase()
}

function statusLabel(s: PendingPosSale): string {
  if (s.status === 'syncing') return 'Syncing'
  if (s.status === 'failed') return 'Needs attention'
  if (s.status === 'pending') return 'Waiting'
  return s.status
}

export function PosSyncQueueDrawer({
  open,
  onOpenChange,
  items,
  isOnline,
  syncInProgress,
  lastSyncAttemptIso,
  onRetry,
  onRetryAllFailed,
  onRemove,
  onApplyStockFix,
}: Props) {
  const failed = items.filter((i) => i.status === 'failed')
  const canRetryAll = isOnline && failed.length > 0 && !syncInProgress

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'bg-card text-card-foreground border-border fixed top-0 right-0 z-[51] flex h-full max-h-[100dvh] w-full max-w-md flex-col border-l shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            'duration-200 motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none',
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-4 pr-12">
            <DialogTitle className="text-lg font-semibold">Sync queue</DialogTitle>
            <DialogPrimitive.Close
              type="button"
              className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-lg p-2 text-lg leading-none opacity-80 transition hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
            >
              <span aria-hidden>×</span>
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
            {items.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center text-sm">No pending sales.</p>
            ) : (
              <ul className="space-y-3 pb-4">
                {items.map((row) => {
                  const lineSummaries = row.payload.items.map((l) => {
                    const name = row.lineLabels.find((x) => x.productId === l.productId)?.name ?? `Product #${l.productId}`
                    return `${l.quantity}× ${name}`
                  })
                  const errText =
                    row.status === 'failed'
                      ? plainLanguageForPosError(row.errorCode, row.lastError ?? 'Sync failed')
                      : null

                  return (
                    <li
                      key={row.localId}
                      className="rounded-xl border border-border bg-muted/30 p-3 text-sm motion-reduce:transition-none"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-mono text-xs font-semibold">#{shortRef(row.idempotencyKey)}</span>
                        <span className="text-muted-foreground text-xs">{new Date(row.createdAt).toLocaleString()}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-muted-foreground text-xs">
                          {statusLabel(row)} · {row.payload.paymentMethod}
                        </p>
                        <span className="text-xs font-semibold tabular-nums">Total {row.totalLabel}</span>
                      </div>
                      <ul className="mt-2 space-y-0.5 text-xs">
                        {lineSummaries.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                      {errText ? <p className="text-destructive mt-2 text-xs leading-snug">{errText}</p> : null}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {row.status === 'failed' && isOnline ? (
                          <Button type="button" size="sm" className="min-h-10 px-4" onClick={() => onRetry(row.localId)}>
                            Retry
                          </Button>
                        ) : null}
                        {row.status === 'failed' && row.errorCode === 'INSUFFICIENT_STOCK_ON_SYNC' && row.conflictDetails ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="min-h-10 px-4"
                            onClick={() => onApplyStockFix(row.localId)}
                          >
                            Reduce to available stock
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="min-h-10 px-4"
                          onClick={() => onRemove(row.localId)}
                        >
                          Remove
                        </Button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="border-t border-border px-4 py-4 space-y-3">
            <Button
              type="button"
              variant="secondary"
              className="min-h-11 w-full"
              disabled={!canRetryAll}
              onClick={() => onRetryAllFailed()}
            >
              Retry all failed
            </Button>
            <p className="text-muted-foreground text-xs">
              Last sync attempt:{' '}
              {lastSyncAttemptIso ? new Date(lastSyncAttemptIso).toLocaleString() : '—'}
            </p>
            {!isOnline ? <p className="text-xs text-amber-700 dark:text-amber-400">You are offline — connect to sync.</p> : null}
            {syncInProgress ? (
              <p className="text-muted-foreground text-xs motion-reduce:animate-none">Syncing…</p>
            ) : null}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
